import React, { useMemo } from 'react';
import { TextField } from '../../../../components/TextField/TextField';
import { useLanguage } from '../../../../locale/useLanguage';
import { Button } from '../../../../components/Button/Button';
import { Switch } from '../../../../components/Switch/Switch';
import { useAddressNoteValidationForm } from '../../../../hooks/validations';
import { useGetCmsConfigsQuery } from '../../../../apis/cmsApi/cms.hooks.api';
import { AddressNoteForm } from '../../../../hooks/validations/useAddressNoteValidationForm';
import { ConciergeForm } from '../../../../components/ConciergeForm/ConciergeForm';
import { AddressInstructions } from '../../../../components/AddressInstructions/AddressInstructions';

import './AddressNotes.css';
import { useOrder } from '../../../../hooks/useOrder';

type AddressNotesProps = {
  notes?: AddressNoteForm;
  isFromChangeAddress?: boolean;
  isUserLogged: boolean;
  onSaveNotes: (notes: AddressNoteForm) => void;
  onSkip: () => void;
};

export const AddressNotes: React.FC<AddressNotesProps> = ({
  notes,
  onSaveNotes,
  onSkip,
  isFromChangeAddress,
  isUserLogged,
}) => {
  const [t] = useLanguage();
  const { data, isLoading } = useGetCmsConfigsQuery(undefined);

  const {
    // premiseTypeField,
    errors,
    floorValidation,
    handleSubmit,
    hasElevatorValidation,
    intercomNameValidation,
    notesValidation,
    // onPremiseTypeChange,
    onIsReceptionChange,
    addressTypeField,
    onAddressTypeChange,
    onReceptionAfternoonFromHourChange,
    onReceptionAfternoonToHourChange,
    onReceptionMorningFromHourChange,
    onReceptionMorningToHourChange,
    onWeekDaysChange,
    receptionAfternoonFromHourField,
    receptionAfternoonToHourField,
    receptionField,
    addressNameValidation,
    isSavingAddressValidation,
    receptionMorningFromHourField,
    receptionMorningToHourField,
    isSavingAddressController,
    weekDaysField,
  } = useAddressNoteValidationForm(notes, data, isFromChangeAddress);

  const order = useOrder();

  let disableReception = false;

  if (isFromChangeAddress && order?.receptionRules?.allowedOrderStatuses) {
    if (
      !order?.receptionRules?.allowedOrderStatuses.includes(order?.mlkStatus)
    ) {
      disableReception = true;
      if (receptionField) {
        onIsReceptionChange(false);
      }
    }
  }

  const saveNotes = useMemo(() => {
    return handleSubmit((formValue) => {
      onSaveNotes(formValue);
    });
  }, [handleSubmit, onSaveNotes]);

  const daysError = errors.days as any;

  return !isLoading ? (
    <>
      <AddressInstructions
        addressType={addressTypeField.value}
        errors={errors}
        floorValidation={floorValidation}
        intercomNameValidation={intercomNameValidation}
        hasElevatorValidation={hasElevatorValidation}
        notesValidation={notesValidation}
        onAddressTypeChange={onAddressTypeChange}
      />
      <div className="address-notes--switch-container">
        <label>{t('addressNotesView.reception')}</label>
        <Switch
          onChange={onIsReceptionChange}
          isChecked={receptionField.value}
          disabled={disableReception}
        />
      </div>
      {receptionField.value && (
        <section className="address-notes--reception-section">
          <ConciergeForm
            isOrderSpecific={!!isFromChangeAddress}
            conciergeAfternoonFromHour={receptionAfternoonFromHourField.value}
            conciergeAfternoonFromHourChange={
              onReceptionAfternoonFromHourChange
            }
            conciergeAfternoonToHour={receptionAfternoonToHourField.value}
            conciergeAfternoonToHourChange={onReceptionAfternoonToHourChange}
            conciergeMorningFromHour={receptionMorningFromHourField.value}
            conciergeMorningFromHourChange={onReceptionMorningFromHourChange}
            conciergeMorningToHour={receptionMorningToHourField.value}
            conciergeMorningToHourChange={onReceptionMorningToHourChange}
            // premiseTypeFieldValue={premiseTypeField.value}
            selectedWeekDays={weekDaysField.value}
            // onPremiseTypeChange={onPremiseTypeChange}
            onWeekDaysChange={onWeekDaysChange}
            conciergeOpeningHoursError={
              errors.receptionMorningFromHour?.message ||
              errors.receptionMorningToHour?.message ||
              errors.receptionAfternoonFromHour?.message ||
              errors.receptionAfternoonToHour?.message
            }
            daysFieldError={daysError?.message}
          />
        </section>
      )}
      {isFromChangeAddress && isUserLogged && (
        <div className="address-notes--checkbox-container">
          <input
            {...isSavingAddressValidation}
            type="checkbox"
            id="saveAddress"
          />
          <label htmlFor="saveAddress">
            {t('addressNotesView.saveAddress')}
          </label>
        </div>
      )}
      {(!isFromChangeAddress || isSavingAddressController.field.value) &&
        isUserLogged && (
          <div className="address-notes--field-container">
            <TextField
              {...addressNameValidation}
              placeholder={t('addressNotesView.addressName')}
              error={errors.addressName?.message}
            />
          </div>
        )}
      <div className="address-notes--buttons-container">
        <Button
          isPrimary
          label={t('addressNotesView.confirm')}
          onClick={saveNotes}
        />
        <div className="address-notes--cancel-button">
          <Button label={t('addressNotesView.skip')} onClick={onSkip} />
        </div>
      </div>
    </>
  ) : (
    <></>
  );
};
